/**
 * SMS Feature — React Query hooks
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type {
  SmsListParams,
  CampaignListParams,
  CampaignMessageParams,
  CreateTemplatePayload,
  UpdateTemplatePayload,
  CreateCampaignPayload,
  RecipientFilters,
} from '@/types'
import {
  fetchSmsMessages,
  fetchSmsMessage,
  fetchSmsTemplates,
  createSmsTemplate,
  updateSmsTemplate,
  deleteSmsTemplate,
  fetchCampaigns,
  fetchCampaign,
  createCampaign,
  sendCampaign,
  cancelCampaign,
  fetchCampaignMessages,
  fetchCampaignAnalytics,
  previewRecipients,
  fetchSmsCredits,
} from './api'

// ── Query key factory ────────────────────────────────────────────────────────

export const smsKeys = {
  messages:          (params: SmsListParams) => ['sms', 'messages', params] as const,
  message:           (id: string) => ['sms', 'messages', id] as const,
  templates:         ['sms', 'templates'] as const,
  campaigns:         (params: CampaignListParams) => ['sms', 'campaigns', params] as const,
  campaign:          (id: string) => ['sms', 'campaigns', id] as const,
  campaignMessages:  (id: string, params: CampaignMessageParams) =>
    ['sms', 'campaign-messages', id, params] as const,
  campaignAnalytics: (id: string) => ['sms', 'campaign-analytics', id] as const,
  credits:           ['sms', 'credits'] as const,
}

// ── SMS Messages ──────────────────────────────────────────────────────────────

export function useSmsMessages(params: SmsListParams) {
  return useQuery({
    queryKey: smsKeys.messages(params),
    queryFn: () => fetchSmsMessages(params),
  })
}

export function useSmsMessage(id: string) {
  return useQuery({
    queryKey: smsKeys.message(id),
    queryFn: () => fetchSmsMessage(id),
    enabled: !!id,
  })
}

// ── SMS Templates ─────────────────────────────────────────────────────────────

export function useSmsTemplates() {
  return useQuery({
    queryKey: smsKeys.templates,
    queryFn: fetchSmsTemplates,
    staleTime: 2 * 60 * 1000,
  })
}

export function useCreateTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateTemplatePayload) => createSmsTemplate(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: smsKeys.templates })
      toast.success('Template created.')
    },
  })
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateTemplatePayload }) =>
      updateSmsTemplate(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: smsKeys.templates })
      toast.success('Template updated.')
    },
  })
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteSmsTemplate(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: smsKeys.templates })
      toast.success('Template deleted.')
    },
  })
}

// ── SMS Campaigns ─────────────────────────────────────────────────────────────

export function useCampaigns(params: CampaignListParams = {}) {
  return useQuery({
    queryKey: smsKeys.campaigns(params),
    queryFn: () => fetchCampaigns(params),
  })
}

export function useCampaign(id: string) {
  return useQuery({
    queryKey: smsKeys.campaign(id),
    queryFn: () => fetchCampaign(id),
    enabled: !!id,
    // Refetch while campaign is actively sending
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'SENDING' ? 5000 : false
    },
  })
}

export function useCreateCampaign() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateCampaignPayload) => createCampaign(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sms', 'campaigns'] })
      toast.success('Campaign created.')
    },
  })
}

export function useSendCampaign(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => sendCampaign(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(smsKeys.campaign(id), updated)
      void queryClient.invalidateQueries({ queryKey: ['sms', 'campaigns'] })
      toast.success('Campaign is sending.')
    },
  })
}

export function useCancelCampaign(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => cancelCampaign(id),
    onSuccess: (updated) => {
      queryClient.setQueryData(smsKeys.campaign(id), updated)
      void queryClient.invalidateQueries({ queryKey: ['sms', 'campaigns'] })
      toast.success('Campaign cancelled.')
    },
  })
}

export function useCampaignMessages(id: string, params: CampaignMessageParams = {}) {
  return useQuery({
    queryKey: smsKeys.campaignMessages(id, params),
    queryFn: () => fetchCampaignMessages(id, params),
    enabled: !!id,
  })
}

export function useCampaignAnalytics(id: string, enabled = true) {
  return useQuery({
    queryKey: smsKeys.campaignAnalytics(id),
    queryFn: () => fetchCampaignAnalytics(id),
    enabled: !!id && enabled,
    // Poll every 5 s while campaign is actively dispatching messages.
    // The parent component passes `enabled` conservatively to avoid
    // unnecessary requests, but interval is the primary throttle here.
    refetchInterval: 5000,
  })
}

export function usePreviewRecipients(filters: RecipientFilters, enabled: boolean) {
  return useQuery({
    queryKey: ['sms', 'recipient-preview', filters],
    queryFn: () => previewRecipients(filters),
    enabled,
    staleTime: 30 * 1000,
  })
}

// ── SMS Credits ───────────────────────────────────────────────────────────────

export function useSmsCredits() {
  return useQuery({
    queryKey: smsKeys.credits,
    queryFn: fetchSmsCredits,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000, // poll every 10 min
  })
}

export function useTopUpCredits() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: import('./api').TopUpPayload) =>
      import('./api').then((m) => m.topUpCredits(payload)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: smsKeys.credits })
      toast.success('Credits added successfully.')
    },
    onError: () => {
      toast.error('Failed to add credits. Please try again.')
    },
  })
}

export function useCreditTransactions(params?: { page?: number; page_size?: number }) {
  return useQuery({
    queryKey: [...smsKeys.credits, 'transactions', params],
    queryFn: () =>
      import('./api').then((m) => m.fetchCreditTransactions(params)),
    staleTime: 60 * 1000,
  })
}
